import { UnitUsageRange } from "@asd/customer-portal-api";
import { alpha } from "@mui/material";
import { Palette, Theme } from "@mui/material/styles";

import { TableToolbar } from "../components";
import { OrganizationKey } from "../utils/orgConfig";

const GRID_ROW_HEIGHT = 36;
const ODD_OPACITY = 0.2;

export const baseTheme = {
  components: {
    MuiAutocomplete: {
      defaultProps: {
        limitTags: 5,
      },
    },
    MuiButton: {
      defaultProps: {
        variant: "contained",
        color: "secondary",
      },
    },
    MuiChip: {
      styleOverrides: {
        label: {
          fontSize: "1.1rem",
        },
      },
    },
    MuiDataGrid: {
      defaultProps: {
        columnHeaderHeight: GRID_ROW_HEIGHT,
        density: "compact",
        disableColumnFilter: true,
        disableColumnMenu: true,
        disableColumnSelector: true,
        getRowClassName: (params: { indexRelativeToCurrentPage: number }) => (params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"),
        initialState: {
          pagination: { paginationModel: { pageSize: 100, page: 0 }, rowCount: 0 },
        },
        pagination: true,
        pageSizeOptions: [5, 10, 50, 100],
        showToolbar: true,
        slots: {
          toolbar: TableToolbar,
        },
        slotProps: {
          toolbar: {
            printOptions: { disableToolbarButton: true },
            showQuickFilter: false,
          },
        },
      },
      styleOverrides: {
        footerContainer: {
          minHeight: GRID_ROW_HEIGHT,
          height: GRID_ROW_HEIGHT,
          paddingBottom: "1px",

          "& .MuiTablePagination-root": {
            overflow: "hidden",

            "& .MuiToolbar-root": {
              minHeight: GRID_ROW_HEIGHT,
              height: GRID_ROW_HEIGHT,

              "& .MuiTablePagination-actions > button": {
                minHeight: GRID_ROW_HEIGHT,
                height: GRID_ROW_HEIGHT,
              },
            },
          },
        },
        root: ({ theme }: { theme: Theme }) => ({
          ".MuiDataGrid-columnHeaderTitleContainerContent": {
            "> .MuiStack-root": {
              overflow: "hidden",

              "> span": {
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              },
            },
          },
          ".MuiDataGrid-mainContent": {
            overflow: "auto",
            fontSize: "1rem",

            "& .MuiDataGrid-main": {
              minHeight: GRID_ROW_HEIGHT * 4,

              [`& .odd`]: {
                "&:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY),
                  "@media (hover: none)": {
                    backgroundColor: "transparent",
                  },
                },
              },
              [`& .oddmaster`]: {
                backgroundColor: theme.palette.grey[theme.palette.mode === "light" ? 300 : 700],
                "&:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY),
                  "@media (hover: none)": {
                    backgroundColor: "transparent",
                  },
                },
              },
              [`& .even`]: {
                backgroundColor: theme.palette.grey[theme.palette.mode === "light" ? 200 : 800],
                "&:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY),
                  "@media (hover: none)": {
                    backgroundColor: "transparent",
                  },
                },
                "&.Mui-selected": {
                  backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY + theme.palette.action.selectedOpacity),
                  "&:hover": {
                    backgroundColor: alpha(
                      theme.palette.primary.main,
                      ODD_OPACITY + theme.palette.action.selectedOpacity + theme.palette.action.hoverOpacity,
                    ),
                    "@media (hover: none)": {
                      backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY + theme.palette.action.selectedOpacity),
                    },
                  },
                },
              },
            },
          },
        }),
        toolbar: ({ theme }: { theme: Theme }) => ({
          height: GRID_ROW_HEIGHT,
          minHeight: GRID_ROW_HEIGHT,

          "& .MuiDataGrid-toolbarQuickFilter .MuiFormControl-root": {
            margin: 0,

            input: {
              padding: `${theme.spacing(1 / 2)} 0`,
            },
          },
        }),
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          margin: 0,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          "&.Mui-selected": {
            backgroundColor: theme.palette.info.main,
          },
        }),
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: "outlined",
        fullWidth: true,
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          height: (theme.mixins.toolbar as Record<string, Record<string, { minHeight: number }>>)["@media (min-width:0px)"][
            "@media (orientation: landscape)"
          ].minHeight,
        }),
      },
    },
    MuiTooltip: {
      defaultProps: {
        disableInteractive: true,
        slotProps: {
          tooltip: {
            sx: {
              fontSize: "1rem",
            },
          },
        },
      },
    },
  },
  typography: {
    fontFamily: "ProximaNova",
    h6: {
      fontFamily: "Questa",
    },
    h5: {
      fontFamily: "Questa",
    },
    h4: {
      fontFamily: "Questa",
    },
    h3: {
      fontFamily: "Questa",
    },
    h2: {
      fontFamily: "Questa",
    },
    h1: {
      fontFamily: "Questa",
    },
    body1: {
      fontSize: "1.1rem",
    },
  },
};

export const OVERRIDE_KEYS = ["footerText", "participantSelectionSelectedBackground"] as const;
export type OverrideKey = (typeof OVERRIDE_KEYS)[number];
export type OverrideSelectors = Record<OverrideKey, (palette: Palette) => string>;

export const UNIT_USAGE_KEYS = Object.values(UnitUsageRange) as UnitUsageRange[];
export type UnitUsageKey = UnitUsageRange;
export type UnitUsageSelectors = Record<UnitUsageKey, (palette: Palette) => string>;

declare module "@mui/material/styles" {
  interface Theme {
    organization: OrganizationKey;
  }

  interface ThemeOptions {
    organization?: OrganizationKey;
  }

  interface Palette {
    custom: Palette["primary"];
    alertinfo: Palette["primary"];
    overrides: Record<OverrideKey, Palette["primary"]>;
    unitUsage: Record<UnitUsageKey, string>;
  }

  interface PaletteOptions {
    custom?: PaletteOptions["primary"];
    alertinfo?: PaletteOptions["primary"];
    overrides?: Partial<Record<OverrideKey, PaletteOptions["primary"]>>;
    unitUsage?: Record<UnitUsageKey, string>;
  }
}

declare module "@mui/material/Alert" {
  interface AlertPropsColorOverrides {
    alertinfo: true;
  }
}

declare module "@mui/material/ToggleButton" {
  interface ToggleButtonPropsColorOverrides {
    custom: true;
  }
}
